import React, { createContext, useContext, useState, useEffect } from 'react';
import useBackendCampaign from '../../hooks/useCampaign';
import campaignService from '../../services/CampaignService';

const CampaignContext = createContext();

export const useCampaign = () => {
  const context = useContext(CampaignContext);
  if (!context) {
    throw new Error('useCampaign must be used within a CampaignProvider');
  }
  return context;
};

const CampaignProvider = ({ 
  children, 
  onNavigateToAutomation, 
  onCampaignSelect: externalOnCampaignSelect,
  onCampaignSelectionChange: externalOnCampaignSelectionChange,
  initialActiveCampaignId,
  initialSelectedCampaignIds = []
}) => {
  // Real campaigns from backend
  const { 
    campaigns: backendCampaigns, 
    loading: campaignsLoading, 
    error: campaignsError,
    loadCampaigns 
  } = useBackendCampaign();
  
  const [campaigns, setCampaigns] = useState([]);

  // Load campaigns from backend on mount
  useEffect(() => {
    console.log('[LOG] [CampaignProvider] Loading campaigns from backend...');
    loadCampaigns().catch(error => {
      console.error('[ERROR] [CampaignProvider] Failed to load campaigns:', error);
    });
  }, [loadCampaigns]);

  // Update campaigns when backend data changes
  useEffect(() => {
    if (backendCampaigns && backendCampaigns.length > 0) {
      console.log('[LOG] [CampaignProvider] Backend campaigns loaded:', backendCampaigns.length);
      console.log('[LOG] [CampaignProvider] Campaign IDs:', backendCampaigns.map(c => c.id));
      setCampaigns(backendCampaigns);
    }
  }, [backendCampaigns]);

  // Trova la campagna iniziale basata sull'ID o usa la prima
  const initialCampaign = initialActiveCampaignId
    ? campaigns.find(c => c.id === initialActiveCampaignId) || campaigns[0]
    : campaigns[0];

  // Stati per gestire sia selezione singola (compatibilità) che multipla
  const [activeCampaign, setActiveCampaign] = useState(initialCampaign);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState(initialSelectedCampaignIds);

  // Set activeCampaign when campaigns are loaded from backend
  useEffect(() => {
    if (campaigns.length > 0 && !activeCampaign) {
      const campaignToSet = initialActiveCampaignId
        ? campaigns.find(c => c.id === initialActiveCampaignId) || campaigns[0]
        : campaigns[0];

      console.log('[LOG] [CampaignProvider] Setting initial active campaign:', campaignToSet);
      console.log('[LOG] [CampaignProvider] Campaign company_id:', campaignToSet?.company_id);
      console.log('[LOG] [CampaignProvider] Campaign company:', campaignToSet?.company);
      console.log('[LOG] [CampaignProvider] Campaign keys:', campaignToSet ? Object.keys(campaignToSet) : 'none');
      setActiveCampaign(campaignToSet);
    }
  }, [campaigns, activeCampaign, initialActiveCampaignId]);

  // Gestisce la selezione singola (compatibilità)
  const handleCampaignSelect = (campaign) => {
    setActiveCampaign(campaign);
    
    // Notifica anche il componente genitore se è disponibile
    if (externalOnCampaignSelect) {
      externalOnCampaignSelect(campaign);
    }
  };

  // Gestisce la selezione multipla
  const handleCampaignSelectionChange = (newSelectedIds, type) => {
    // Aggiorna immediatamente lo stato
    setSelectedCampaignIds(newSelectedIds);
    
    // Aggiorna anche la campagna attiva per compatibilità
    if (newSelectedIds.length === 1) {
      const selectedCampaign = campaigns.find(c => c.id === newSelectedIds[0]);
      if (selectedCampaign) {
        setActiveCampaign(selectedCampaign);
      }
    } else if (newSelectedIds.length === 0) {
      // Se non c'è selezione, mantieni la prima campagna come attiva
      setActiveCampaign(campaigns[0]);
    }
    
    // Notifica il componente genitore
    if (externalOnCampaignSelectionChange) {
      externalOnCampaignSelectionChange(newSelectedIds, type);
    }
  };

  const handleCreateCampaign = () => {
    console.log('Navigazione al pannello automation per creare nuova campagna');
    // Se è disponibile la funzione di navigazione, la usa
    if (onNavigateToAutomation) {
      onNavigateToAutomation();
    } else {
      console.log('Apertura modal per creare nuova campagna');
      // Fallback per il comportamento precedente
    }
  };

  // Funzioni utility per la selezione multipla
  const getSelectedCampaigns = () => {
    return campaigns.filter(campaign => selectedCampaignIds.includes(campaign.id));
  };

  // "Tutte le campagne" è selezionato solo quando tutti gli ID sono presenti nell'array
  const isAllCampaignsSelected = selectedCampaignIds.length > 0 && selectedCampaignIds.length === campaigns.length;

  const getDisplayText = () => {
    if (selectedCampaignIds.length === 0) {
      return 'Nessuna campagna selezionata';
    } else if (isAllCampaignsSelected) {
      return `Tutte le campagne (${campaigns.length})`;
    } else if (selectedCampaignIds.length === 1) {
      const campaign = campaigns.find(c => c.id === selectedCampaignIds[0]);
      return campaign ? campaign.name : 'Campagna non trovata';
    } else {
      return `${selectedCampaignIds.length} campagne selezionate`;
    }
  };

  const value = {
    // Stati esistenti (compatibilità)
    campaigns,
    activeCampaign,
    handleCampaignSelect,
    handleCreateCampaign,
    
    // Nuovi stati per selezione multipla
    selectedCampaignIds,
    handleCampaignSelectionChange,
    getSelectedCampaigns,
    isAllCampaignsSelected,
    getDisplayText,
    
    // Backend integration states
    campaignsLoading,
    campaignsError,
    loadCampaigns,
    
    // Backward compatibility
    selectedCampaignId: selectedCampaignIds.length === 1 ? selectedCampaignIds[0] : null
  };

  return (
    <CampaignContext.Provider value={value}>
      {children}
    </CampaignContext.Provider>
  );
};

export default CampaignProvider; 